import { inboxApiHttp } from '@/services/api/tattle-http'
import type {
  InboxClearResponse,
  InboxComposeBody,
  InboxComposeResponse,
  InboxConfig,
  InboxDetailResponse,
  InboxListParams,
  InboxListResponse,
  InboxStats,
  InboxWaitForBody,
  InboxWaitForResponse
} from './email-testing.types'

const buildQuery = (params: InboxListParams = {}) => {
  const usp = new URLSearchParams()
  if (params.to) usp.set('to', params.to)
  if (params.since) usp.set('since', params.since)
  if (params.subject) usp.set('subject', params.subject)
  if (params.merchantId !== undefined) usp.set('merchantId', String(params.merchantId))
  if (params.customerId !== undefined) usp.set('customerId', String(params.customerId))
  if (params.q) usp.set('q', params.q)
  if (params.cursor) usp.set('cursor', params.cursor)
  if (params.limit !== undefined) usp.set('limit', String(params.limit))
  const qs = usp.toString()
  return qs ? `?${qs}` : ''
}

export const emailTestingAPI = {
  config: () => inboxApiHttp.get<InboxConfig>('/config'),

  /**
   * Per-day email + SMS usage counts over the trailing `days` window
   * (server-side aggregation). Omit `days` to use the sink's default
   * (its retention window).
   */
  stats: (days?: number) =>
    inboxApiHttp.get<InboxStats>(`/stats${days !== undefined ? `?days=${days}` : ''}`),

  list: (params: InboxListParams = {}) =>
    inboxApiHttp.get<InboxListResponse>(`/messages${buildQuery(params)}`),

  get: (id: string) => inboxApiHttp.get<InboxDetailResponse>(`/messages/${id}`),

  clear: (to: string) =>
    inboxApiHttp.delete<InboxClearResponse>(`/messages?to=${encodeURIComponent(to)}`),

  /**
   * Inject a synthetic message. Used by the compose / reply / forward UI.
   * Backend validates recipient domains server-side and returns 422 if any
   * address isn't on an allowlisted internal domain.
   */
  compose: (body: InboxComposeBody) =>
    inboxApiHttp.post<InboxComposeResponse>('/messages', body),

  /**
   * Long-poll for the next message matching a recipient. Returns as soon as
   * a matching message arrives or the sink's timeoutMs elapses. Powers the
   * list view's live-tail when the user has filtered to a specific recipient.
   */
  waitFor: (body: InboxWaitForBody) =>
    inboxApiHttp.post<InboxWaitForResponse>('/wait-for', body),

  aiDraft: (body: {
    mode: 'compose' | 'reply'
    channel: 'email' | 'sms'
    perspective: 'merchant' | 'customer' | 'internal'
    tone?: string
    goal?: string
    originalSubject?: string
    originalBody?: string
    originalFrom?: string
    originalTo?: string
    label?: string
    userHint?: string
    merchantName?: string
  }) => inboxApiHttp.post<{ draft: string; model: string; usage: { input_tokens: number; output_tokens: number } }>('/ai/draft', body),

  attachmentBlob: async (id: string, index: number) => {
    const response = await inboxApiHttp.get(`/messages/${id}/attachments/${index}`, {
      responseType: 'blob'
    })
    return response.data as Blob
  }
}
